import _, { toInteger } from 'lodash';
import React, { ReactNode, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { cancelLeaseApplication } from '../../../api/instant-lease-api';
import {
  fetchCarDetails,
  fetchLeaseApp,
} from '../../../redux/leaseApp/lease-app-slice';
import { useAppDispatch } from '../../../redux/store';
import { RootState } from '../../../redux/types';
import { CarData, Type } from '../../../types/instant-lease-api';
import {
  canCancelLeaseApplication,
  formatGermanPrice,
  getIsAllowedToBeCanceled,
} from '../../../utils/getter';
import { ReactComponent as ArrowRight } from '../../b2b/components/icons/chevron-right.svg';
import ConfirmationModal from '../../b2c/components/confirmation-modal/confirmation-modal';
import { ReactComponent as CarFrontIcon } from '../icons/car-front-64.svg';
import {
  getFuelType,
  getGearBoxType,
  getVehicleBody,
  getVehicleType,
} from './car-data-mapper';

// Responsive breakpoints used by the Fahrzeugübersicht design:
//   mobile  <= 599px  -> max-[600px]:
//   tablet  600-1439px -> min-[600px]:max-[1440px]:
//   both    <= 1439px -> max-[1440px]:
// Body text: text-300 (18/24) on desktop, text-200 (16/20) below 1440px.
// Sizes come from the global typography tokens (src/styles/typography.css).
const bodyText =
  'text-body-300 max-[1440px]:text-body-200';
// Same as bodyText, forced over the global `p` rules.
const paragraphText =
  'text-body-300! max-[1440px]:text-body-200!';
// Small text: text-200 (16/20) on desktop, text-100 (14/20) below 1440px.
const labelText =
  'text-body-200 max-[1440px]:text-body-100';

export interface VehicleOverviewProps {
  /**
   * Show the "Leasingantrag abbrechen" link at the bottom of the data column.
   * Ignored when a custom `action` is provided.
   */
  showCancelButton?: boolean;
  /** Car data to display. Falls back to the car data from the store. */
  carInfo?: CarData;
  /**
   * Custom action rendered at the bottom of the data column
   * (e.g. the "Weiter" CTA on the bootstrap page).
   */
  action?: ReactNode;
  /**
   * Fetch the car details of the active lease application on mount.
   * Disable it when the parent already fetches the car details itself.
   */
  fetchOnMount?: boolean;
  className?: string;
}

interface OverviewRow {
  label: string;
  value: ReactNode;
}

const OverviewSection: React.FC<{
  title: string;
  total?: string;
  rows: OverviewRow[];
  testId?: string;
}> = ({ title, total, rows, testId }) => (
  <section className='flex flex-col gap-2' data-testid={testId}>
    <div className='flex items-center justify-between gap-4 font-bold text-(--vo-text)'>
      <span>{title}</span>
      {total !== undefined && <span>{total}</span>}
    </div>
    {rows.map(({ label, value }) => (
      <div className='flex items-center justify-between gap-4' key={label}>
        <span className='text-(--vo-muted)'>{label}</span>
        <span className='text-right whitespace-nowrap text-(--vo-text)'>
          {value}
        </span>
      </div>
    ))}
  </section>
);

/**
 * "Fahrzeugübersicht" – shared vehicle and contract overview used by the
 * sub-header drawer ("Mein Fahrzeug") and the bootstrap page.
 */
const VehicleOverview: React.FC<VehicleOverviewProps> = ({
  showCancelButton = true,
  carInfo,
  action,
  fetchOnMount = true,
  className = '',
}) => {
  // Lease Applications Store
  const { carData, activeApplication } = useSelector(
    (state: RootState) => state.leaseApp,
  );

  // Auth Store
  const { emailVerified, accessToken } = useSelector(
    (state: RootState) => state.auth,
  );

  // History hook
  const navigate = useNavigate();

  // Dispatch hook
  const dispatch = useAppDispatch();

  // Confirmation modal control
  const [confirmationVisibility, setConfirmationVisibility] =
    useState<boolean>(false);

  // Cancellation Loading
  const [loading, setLoading] = useState<boolean>(false);

  // Format price with german price format
  const { format } = formatGermanPrice();

  const carDetails = carInfo ?? carData;

  // B2C applications show gross prices, B2B applications net prices
  const isGross = activeApplication?.type === Type.B2C;
  const vehicleData = activeApplication?.offer?.vehicle_data;
  const leasingOptions = activeApplication?.offer?.leasing_options;

  // handle flex leasing application
  const { partner, offer } = activeApplication || {};
  const { armada_id: offerId, publication } = offer || {};
  const { publication_id: publicationId, pricing_id: pricingId } =
    publication || {};

  useEffect(() => {
    if (!fetchOnMount || (!offerId && !(publicationId && pricingId))) {
      return;
    }
    dispatch(fetchCarDetails({ partner, publicationId, pricingId, offerId }));
  }, [fetchOnMount, partner, publicationId, pricingId, offerId, dispatch]);

  // Values
  const vehicleState =
    carDetails?.carVehicleState ||
    (activeApplication
      ? activeApplication.vin === ''
        ? 'NEU'
        : 'USED'
      : undefined);

  const fuelType = carDetails?.carFuelType;
  const usedMileage = carDetails?.carUsedMileage;
  const image = carDetails?.defaultImage || vehicleData?.vehicleImageUrl;

  const totalListPrice =
    carDetails?.totalListPrice ||
    toInteger(vehicleData?.emission_data?.totalPrice);
  const duration = carDetails?.duration || leasingOptions?.duration;
  const mileage = carDetails?.mileageYear || leasingOptions?.mileage;

  const downPayment =
    carDetails?.downPayment ??
    (isGross
      ? leasingOptions?.downPayment?.gross
      : leasingOptions?.downPayment?.net);

  const additionalCost = {
    freight:
      carDetails?.additionalCost?.freight ||
      vehicleData?.additional_cost?.freight,
    logistics:
      carDetails?.additionalCost?.logistics ||
      vehicleData?.additional_cost?.logistics,
    registration:
      carDetails?.additionalCost?.registration ||
      vehicleData?.additional_cost?.registration,
    returnFreight:
      carDetails?.additionalCost?.returnFreight ||
      vehicleData?.additional_cost?.returnFreight,
  };

  const isRateNet = activeApplication?.type
    ? activeApplication.type === Type.B2B
    : carDetails?.commercial;
  const monthlyRate =
    (isRateNet
      ? leasingOptions?.totals?.rate?.net
      : leasingOptions?.totals?.rate?.gross) || carDetails?.rate;

  const isFlex = carDetails?.product === 'FLEX';

  // Fahrzeugzustand
  const conditionLine =
    vehicleState === 'USED'
      ? `Gebrauchtwagen${
          usedMileage ? ` | ${usedMileage.toLocaleString('de-DE')} km` : ''
        }`
      : vehicleState
        ? 'Neu'
        : undefined;

  const fuelLabel = fuelType ? getFuelType(fuelType) : undefined;

  const co2 = carDetails?.carCo2;
  const consumption =
    carDetails?.carFuelEconomyCombined ||
    carDetails?.carPowerConsumptionCombined;
  const emissionTest =
    carDetails?.carConsumptionAndEmissionTest ||
    vehicleData?.emission_data?.consumptionAndEmissionTest;
  const consumptionUnit = fuelType === 'ELECTRIC' ? 'kWh' : 'l';
  const emissionLine =
    co2 !== undefined && co2 !== null
      ? `${co2.toLocaleString('de-DE')} g/km (komb.)*, ${(
          consumption || 0
        ).toLocaleString(
          'de-DE',
        )} ${consumptionUnit}/100 km (komb.)*${emissionTest ? ` ${emissionTest}` : ''}`
      : undefined;

  // Vertragsdaten
  const contractRows: OverviewRow[] = [
    ...(totalListPrice
      ? [{ label: 'Fahrzeuggesamtpreis', value: format(totalListPrice) }]
      : []),
    ...(duration
      ? [{ label: 'Deine Laufzeit', value: `${duration} Monate` }]
      : []),
    ...(mileage
      ? [
          {
            label: 'Deine Laufleistung',
            value: `${Math.floor(mileage).toLocaleString('de-DE')} km/Jahr`,
          },
        ]
      : []),
  ];

  // Einmalkosten
  const oneTimeCosts: { label: string; amount: number }[] = [
    { label: 'Sonderzahlung', amount: downPayment || 0 },
    { label: 'Rückholkosten', amount: additionalCost.returnFreight || 0 },
    { label: 'Zulassungskosten', amount: additionalCost.registration || 0 },
    { label: 'Überführungskosten', amount: additionalCost.freight || 0 },
    ...(additionalCost.logistics
      ? [{ label: 'Logistik', amount: additionalCost.logistics }]
      : []),
  ];
  const oneTimeTotal = _.sumBy(oneTimeCosts, 'amount');

  // Monatliche Kosten
  const services = carDetails?.services;
  const monthlyRows: OverviewRow[] = [
    { label: 'Finanz-/Leasingrate', value: format(services?.finance || 0) },
    { label: 'Servicerate', value: format(services?.technic || 0) },
    { label: 'Fuel&Charge-Karte', value: format(services?.fuelCard || 0) },
    { label: 'Versicherungsrate', value: format(services?.insurance || 0) },
  ];

  // Ausstattung
  const features: OverviewRow[] = [
    {
      label: 'Fahrzeugart',
      value: vehicleState ? getVehicleType(vehicleState) : '-',
    },
    {
      label: 'Beschleunigung',
      value: carDetails?.carAccelerationToHundredKph
        ? `${Number(carDetails.carAccelerationToHundredKph).toLocaleString(
            'de-DE',
            { maximumFractionDigits: 1 },
          )} s (0–100 km/h)`
        : '-',
    },
    {
      label: 'Karosserie',
      value: carDetails?.carBodyType
        ? getVehicleBody(carDetails.carBodyType)
        : '-',
    },
    { label: 'Farbe', value: carDetails?.carColorName || '-' },
    { label: 'Kraftstoff', value: fuelLabel || '-' },
    { label: 'Türen', value: carDetails?.carNumberOfDoors || '-' },
    {
      label: 'Getriebe',
      value: carDetails?.carTransmissionType
        ? getGearBoxType(carDetails.carTransmissionType)
        : '-',
    },
    {
      label: 'Leistung',
      value:
        carDetails?.carPowerKiloWatt && carDetails?.carHorsePower
          ? `${carDetails.carPowerKiloWatt} kW | ${carDetails.carHorsePower} PS`
          : '-',
    },
    {
      label: 'Geschwindigkeit',
      value: carDetails?.carMaxSpeed ? `${carDetails.carMaxSpeed} km/h` : '-',
    },
  ];

  const showLeasingCancelButton =
    !action &&
    showCancelButton &&
    emailVerified &&
    canCancelLeaseApplication(activeApplication?.state);

  // Handle cancel lease
  const cancelLeaseApp = () => {
    setLoading(true);
    if (getIsAllowedToBeCanceled(activeApplication?.state)) {
      cancelLeaseApplication(accessToken, activeApplication?.uuid as string)
        .then(() => {
          dispatch(
            fetchLeaseApp({
              accessToken,
              leaseAppId: activeApplication?.uuid as string,
            }),
          ).then(() => {
            setLoading(false);
            setConfirmationVisibility(false);
            navigate('/');
          });
        })
        .catch(() => {
          setLoading(false);
        });
    }
  };

  return (
    <div
      className={`mx-auto flex w-full max-w-[784px] items-start gap-12 text-left font-text font-normal text-(--vo-text) [--vo-muted:var(--vel-color-neutral-600,#757777)] [--vo-text:var(--vel-color-secondary-400,#0f3549)] max-[600px]:max-w-none max-[600px]:flex-col max-[600px]:items-stretch max-[600px]:gap-6 min-[600px]:max-[1440px]:max-w-[756px] min-[600px]:max-[1440px]:gap-8 ${bodyText} ${className}`.trim()}
      data-testid='vehicle-overview'
    >
      <div className='flex min-w-0 flex-[0_0_336px] flex-col gap-8 max-[600px]:w-full max-[600px]:flex-auto min-[600px]:max-[1440px]:flex-1'>
        {(conditionLine || fuelLabel || emissionLine) && (
          <div className='flex flex-col gap-1'>
            {conditionLine && (
              <p className={`m-0! text-(--vo-text)! ${paragraphText}`}>
                {conditionLine}
              </p>
            )}
            {fuelLabel && (
              <p className={`m-0! text-(--vo-text)! ${paragraphText}`}>
                {fuelLabel}
              </p>
            )}
            {emissionLine && (
              <p className='text-tight-100 m-0! text-(--vo-muted)!'>
                {emissionLine}
              </p>
            )}
          </div>
        )}

        {contractRows.length > 0 && (
          <OverviewSection
            title='Vertragsdaten'
            rows={contractRows}
            testId='vehicle-overview-contract'
          />
        )}

        <OverviewSection
          title='Einmalkosten'
          total={format(oneTimeTotal)}
          rows={oneTimeCosts.map(({ label, amount }) => ({
            label,
            value: format(amount),
          }))}
          testId='vehicle-overview-one-time-costs'
        />

        {!isFlex && (
          <OverviewSection
            title='Monatliche Kosten'
            total={format(monthlyRate || 0)}
            rows={monthlyRows}
            testId='vehicle-overview-monthly-costs'
          />
        )}

        {showLeasingCancelButton && (
          <button
            type='button'
            className={`cursor-pointer self-start border-0 bg-transparent p-0 font-text font-bold text-[#dc1e4f] hover:underline focus:outline-none focus-visible:rounded-[20px] focus-visible:shadow-[0_0_0_2px_#0065ff] ${bodyText}`}
            onClick={() => setConfirmationVisibility(true)}
          >
            {isFlex ? 'Auto-Abo abbrechen' : 'Leasingantrag abbrechen'}
          </button>
        )}

        {action && <div className='w-full'>{action}</div>}
      </div>

      <div className='flex min-w-0 flex-[0_0_400px] font-text flex-col gap-6 max-[600px]:-order-1 max-[600px]:w-full max-[600px]:flex-auto min-[600px]:max-[1440px]:flex-1'>
        {_.isEmpty(image) ? (
          <div
            className='flex h-[200px] w-full items-center justify-center bg-[#eaf7f7] max-[600px]:h-40 [&_svg]:size-16 [&_svg]:text-(--vo-text)'
            role='img'
            aria-label='Kein Fahrzeugbild verfügbar'
          >
            <CarFrontIcon aria-hidden='true' />
          </div>
        ) : (
          <div
            className='flex h-[200px] w-full items-center justify-center bg-contain bg-center bg-no-repeat max-[600px]:h-40'
            role='img'
            aria-label={carDetails?.carFullName || 'Fahrzeugbild'}
            style={{ backgroundImage: `url(${image})` }}
          />
        )}
        <dl className='m-0 flex flex-wrap items-start gap-x-4 gap-y-5 '>
          {features.map(({ label, value }) => (
            <div
              className='flex w-[calc((100%_-_32px)/3)] min-w-0 flex-col gap-0.5  max-[600px]:w-[calc((100%_-_16px)/2)]'
              key={label}
            >
              <dt className={`text-(--vo-muted) ${labelText}`}>
                {label}
              </dt>
              <dd className={`m-0 wrap-anywhere text-(--vo-text) ${bodyText}`}>
                {value}
              </dd>
            </div>
          ))}
        </dl>
      </div>

      <ConfirmationModal
        isOpen={confirmationVisibility}
        onClose={() => setConfirmationVisibility(false)}
        confirmationText='Möchtest du deinen Leasingantrag stornieren?'
        cancel={() => setConfirmationVisibility(false)}
        onLoading={loading}
        success={cancelLeaseApp}
      >
        <ul className='arrow-list'>
          <li className='arrow-list-item'>
            <ArrowRight className='arrow-list-icon' />
            <div className='text-loose-200 text-(--vo-text)'>
              <div>
                Dein Antrag wird beendet und das Fahrzeug ist nicht mehr für
                dich reserviert.
              </div>
              <div>
                Du wirst zu mobility.ayvens.com weitergeleitet und kannst ein
                anderes Fahrzeug auswählen.
              </div>
            </div>
          </li>
          <li className='arrow-list-item'>
            <ArrowRight className='arrow-list-icon' />
            <p className='text-loose-200 text-(--vo-text)!'>
              Du kehrst zu deinem Leasingantrag zurück und setzt ihn mit den
              aktualisierten Konditionen fort.
            </p>
          </li>
        </ul>
      </ConfirmationModal>
    </div>
  );
};

export default VehicleOverview;
